import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Loader2, Sparkle } from "lucide-react";

/**
 * One-Tap Sovereign Auth — the landing point for the REAL Sign in with Apple redirect flow.
 * The backend (/api/auth/apple/callback) already verified the identity token, ran the
 * Sovereign ID bridge, and handed us a ready-to-use session token via the URL. There is no
 * password-creation screen here by design — we adopt the token and go straight to the
 * Maison Studio (Design Studio), exactly as Guideline 4.8 / One-Tap Access requires.
 */
const AppleAuthComplete = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { completeAppleSession } = useAuth();
  const [error, setError] = useState("");
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const params = new URLSearchParams(location.search);
    const token = params.get("token");
    const next = params.get("next") || "/cards";
    const appleError = params.get("apple_error");
    if (appleError) {
      navigate(`/login?apple_error=${encodeURIComponent(appleError)}`, { replace: true });
      return;
    }
    if (!token) {
      setError("No Apple session token was received. Please try Sign in with Apple again.");
      return;
    }
    (async () => {
      try {
        await completeAppleSession(token);
        navigate(next, { replace: true });
      } catch (e) {
        setError("Could not complete Sign in with Apple. Please try again.");
      }
    })();
  }, [location.search, navigate, completeAppleSession]);

  return (
    <div className="min-h-screen flex items-center justify-center p-6" data-testid="apple-auth-complete">
      <div className="glass rounded-2xl p-10 text-center max-w-sm">
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center mx-auto mb-5">
          <Sparkle className="w-6 h-6 text-white" />
        </div>
        {error ? (
          <>
            <p className="text-red-400 text-sm mb-4" data-testid="apple-auth-complete-error">{error}</p>
            <button onClick={() => navigate("/login")} className="text-primary text-sm hover:underline">Back to Sign In</button>
          </>
        ) : (
          <>
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-3 text-primary" />
            <p className="text-muted-foreground text-sm">Sealing your Sovereign session — entering the Maison Studio…</p>
          </>
        )}
      </div>
    </div>
  );
};

export default AppleAuthComplete;
